import React, { useEffect, useRef, useState } from 'react';
import type { ChatMessage, UserProfile } from '@/types';
import { globalChatService } from '@/services/globalChatService';
import { chatTranslationService } from '@/services/chatTranslationService';
import { levelUpService } from '@/services/levelUpService';
import { autonomousEcosystemService } from '@/services/autonomousEcosystemService';

interface GlobalChatDockProps {
  userProfile: UserProfile | null;
  realmId?: string | null; // e.g., cartridgeId or instance id
  currentLocation?: string;
  recentNarrative?: string;
  worldKarma?: number;
  companionsPresent?: string[];
}

export const GlobalChatDock: React.FC<GlobalChatDockProps> = ({ 
  userProfile, 
  realmId = null,
  currentLocation = 'neo-tokyo-plaza',
  recentNarrative = '',
  worldKarma = 0,
  companionsPresent = ['JAX', 'Lyra']
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [tab, setTab] = useState<'world' | 'realm'>('world');
  const [worldMsgs, setWorldMsgs] = useState<ChatMessage[]>([]);
  const [realmMsgs, setRealmMsgs] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationPreview, setTranslationPreview] = useState<string>('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    globalChatService.initRealm(realmId || null);
    const unsubWorld = globalChatService.subscribe('world', (m) => setWorldMsgs(prev => [...prev, m]));
    const unsubRealm = globalChatService.subscribe('realm', (m) => setRealmMsgs(prev => [...prev, m]));
    // Seed from buffers
    setWorldMsgs(globalChatService.getBuffer('world'));
    setRealmMsgs(globalChatService.getBuffer('realm'));
    return () => { unsubWorld(); unsubRealm(); };
  }, [realmId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 999999, behavior: 'smooth' });
  }, [worldMsgs.length, realmMsgs.length, tab]);

  const activeMsgs = tab === 'world' ? worldMsgs : realmMsgs;

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !userProfile) return;
    
    setIsTranslating(true);
    
    try {
      // Translate the player input into character dialogue
      const translationContext = {
        playerArchetype: userProfile.selectedArchetype || 'cyberpunk-hacker',
        currentLocation,
        recentNarrative,
        worldKarma,
        isInRealm: tab === 'realm',
        companionsPresent
      };
      
      const translated = await chatTranslationService.translatePlayerInput(
        input.trim(),
        userProfile,
        translationContext
      );
      
      // Validate the message
      const validation = chatTranslationService.validateMessage(translated, translationContext);
      if (!validation.isValid) {
        console.warn('[Chat] Message validation failed:', validation.reason);
        // Show error to user
        setTranslationPreview(`❌ ${validation.reason}`);
        setTimeout(() => setTranslationPreview(''), 3000);
        setIsTranslating(false);
        return;
      }
      
      // Calculate karma impact
      const karmaImpact = chatTranslationService.calculateKarmaImpact(translated, translationContext);
      
      // Send the translated message
      const messageToSend = translated.isAction ? 
        `*${translated.translatedDialogue}*` : // Actions in italics
        translated.translatedDialogue;
      
      await globalChatService.send(tab, messageToSend, userProfile);
      
      // Award experience for social interaction
      const experienceGained = levelUpService.calculateExperienceGain('social_interaction', {
        karmaBonus: Math.abs(karmaImpact),
        isFirstTime: false
      });
      
      if (experienceGained > 0) {
        await levelUpService.awardExperience(userProfile, experienceGained, 'chat_interaction');
      }
      
      // Update agent relationships based on karma
      if (karmaImpact !== 0) {
        companionsPresent.forEach(companion => {
          autonomousEcosystemService.updateAgentRelationship(
            companion.toLowerCase(), 
            'player', 
            karmaImpact
          );
        });
      }
      
      setInput('');
      setTranslationPreview('');
      
    } catch (error) {
      console.error('[Chat] Translation failed:', error);
      setTranslationPreview('❌ Translation failed. Sending original message...');
      
      // Fallback: send original message
      setTimeout(async () => {
        await globalChatService.send(tab, input.trim(), userProfile);
        setInput('');
        setTranslationPreview('');
      }, 1000);
    }
    
    setIsTranslating(false);
  };
  
  // Real-time translation preview
  useEffect(() => {
    if (!input.trim() || !userProfile) {
      setTranslationPreview('');
      return;
    }
    
    const debounceTimer = setTimeout(async () => {
      try {
        const translationContext = {
          playerArchetype: userProfile.selectedArchetype || 'cyberpunk-hacker',
          currentLocation,
          recentNarrative,
          worldKarma,
          isInRealm: tab === 'realm',
          companionsPresent
        };
        
        const translated = await chatTranslationService.translatePlayerInput(
          input.trim(),
          userProfile,
          translationContext
        );
        
        const preview = translated.isAction ? 
          `*${translated.translatedDialogue}*` : 
          translated.translatedDialogue;
          
        setTranslationPreview(preview);
      } catch (error) {
        setTranslationPreview('');
      }
    }, 1000); // 1 second debounce
    
    return () => clearTimeout(debounceTimer);
  }, [input, userProfile, currentLocation, worldKarma, tab]);

  return (
    <div className="fixed bottom-3 right-3 z-40 w-[min(380px,90vw)] text-sm">
      <div className="bg-slate-900/80 backdrop-blur border border-slate-700 rounded-lg shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-slate-800 border-b border-slate-700">
          <div className="flex gap-2">
            <button className={`px-2 py-1 rounded ${tab==='world'?'bg-cyan-600 text-white':'text-slate-300 hover:bg-slate-700'}`} onClick={() => setTab('world')}>World</button>
            <button className={`px-2 py-1 rounded ${tab==='realm'?'bg-fuchsia-600 text-white':'text-slate-300 hover:bg-slate-700'}`} onClick={() => setTab('realm')}>Realm</button>
          </div>
          <button className="text-slate-300 hover:text-white" onClick={() => setIsOpen(v=>!v)}>{isOpen? '—' : '+'}</button>
        </div>

        {isOpen && (
          <>
            <div ref={scrollRef} className="max-h-64 overflow-y-auto px-3 py-2 space-y-2">
              {activeMsgs.length === 0 && (
                <div className="text-slate-500 italic">No messages yet. Say something and your persona will translate it.</div>
              )}
              {activeMsgs.map(m => (
                <div key={m.id} className="flex gap-2">
                  <span className="text-cyan-300 font-semibold min-w-[80px] truncate">{m.sender}</span>
                  <span className="text-slate-200">{m.text}</span>
                </div>
              ))}
            </div>
            {/* Translation Preview */}
            {translationPreview && (
              <div className="px-3 py-2 border-t border-slate-700 bg-slate-800/50">
                <div className="text-xs text-slate-400 mb-1">Your character will say:</div>
                <div className="text-sm text-cyan-300 italic">{translationPreview}</div>
              </div>
            )}
            
            <form onSubmit={send} className="flex items-center gap-2 p-2 border-t border-slate-700">
              <input
                value={input}
                onChange={e=>setInput(e.target.value)}
                placeholder={tab === 'realm' ? "Actions and dialogue..." : "Type and your persona will speak…"}
                className="flex-1 bg-slate-800 text-slate-200 rounded px-2 py-1 outline-none focus:ring-2 focus:ring-cyan-600"
                disabled={isTranslating}
              />
              <button 
                type="submit" 
                className="px-3 py-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isTranslating || !input.trim()}
              >
                {isTranslating ? '...' : 'Send'}
              </button>
            </form>
            
            {/* Chat Instructions */}
            <div className="px-3 py-1 text-xs text-slate-500 border-t border-slate-700">
              {tab === 'world' ? 
                '💬 World chat: Lower karma impact, dialogue only' : 
                '⚡ Realm chat: Higher karma impact, actions possible'
              }
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GlobalChatDock;

